import { createContext, useContext, useSyncExternalStore } from 'react';

import {
  breakdownColors,
  darkBreakdownColors,
  darkPlotColors,
  plotColors,
  subscribePaletteChange,
} from './plot-colors';

export type ChartTheme = 'light' | 'dark';

export const ChartThemeOverrideContext = createContext<ChartTheme | null>(null);

/**
 * Consumers without a `data-theme` marker (the standalone charts app, Storybook
 * before its decorator runs, jsdom) resolve to `light` — the legacy chart
 * appearance is gone, so light IS the default.
 */
function readChartTheme(): ChartTheme {
  if (typeof document === 'undefined') {
    return 'light';
  }
  const marker =
    document.body.getAttribute('data-theme') ??
    document.documentElement.getAttribute('data-theme');
  return marker === 'dark' ? 'dark' : 'light';
}

let currentTheme: ChartTheme = readChartTheme();
let version = 0;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  for (const listener of listeners) {
    listener();
  }
}

if (typeof document !== 'undefined') {
  const observer = new MutationObserver(() => {
    const nextTheme = readChartTheme();
    if (nextTheme !== currentTheme) {
      currentTheme = nextTheme;
      emit();
    }
  });
  const options: MutationObserverInit = {
    attributes: true,
    attributeFilter: ['data-theme'],
  };
  observer.observe(document.documentElement, options);
  observer.observe(document.body, options);
  subscribePaletteChange(emit);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): number {
  return version;
}

function getServerSnapshot(): number {
  return 0;
}

export function useChartTheme() {
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const override = useContext(ChartThemeOverrideContext);

  const theme = override ?? currentTheme;
  const palette = theme === 'dark' ? darkPlotColors : plotColors;
  const getPlotColor = (index: number): string =>
    palette[index % palette.length] ?? palette[0] ?? '#7dbdf0';

  const breakdownPalette =
    theme === 'dark' ? darkBreakdownColors : breakdownColors;

  return { theme, palette, getPlotColor, breakdownPalette };
}
